import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ZillowIcon } from './icons/ZillowIcon';
import { RealtorIcon } from './icons/RealtorIcon';
import { HomesIcon } from './icons/HomesIcon';
import { RedfinIcon } from './icons/RedfinIcon';
import { TruliaIcon } from './icons/TruliaIcon';
import { MLSIcon } from './icons/MLSIcon';
import { Instagram, Facebook, Youtube } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { logger } from '@/lib/logger';

interface ListingPerformanceProps {
  northId: string;
  zillowViews: number;
  primaryColor: string | null;
  secondaryColor: string | null;
  forceMobileLayout?: boolean;
  preloadedStats?: PlatformStats | null; // Optional pre-loaded stats to avoid fetching
}

interface PlatformStats {
  zillow: number;
  zillowSaves: number;
  realtor: number;
  realtorSaves: number;
  homes: number;
  homesSaves: number;
  redfin: number;
  redfinSaves: number;
  trulia: number;
  truliaSaves: number;
  mls: number;
  mlsSaves: number;
  instagram: number;
  facebook: number;
  youtube: number;
  youtubeViews: number;
}

export const ListingPerformance = ({ 
  northId, 
  zillowViews, 
  primaryColor,
  secondaryColor,
  forceMobileLayout = false,
  preloadedStats = null
}: ListingPerformanceProps) => {
  const isMobile = useIsMobile();
  const shouldUseMobileLayout = forceMobileLayout || isMobile;
  
  // Static demo data for instant loading
  const isDemo = northId === 'aaaaaaaa-0000-0000-0000-000000000001';
  const DEMO_STATS: PlatformStats = {
    zillow: 1249,
    zillowSaves: 47,
    realtor: 856,
    realtorSaves: 32,
    homes: 634,
    homesSaves: 21,
    redfin: 478,
    redfinSaves: 18,
    trulia: 312,
    truliaSaves: 14,
    mls: 521,
    mlsSaves: 19,
    instagram: 2340,
    facebook: 1890,
    youtube: 1250,
    youtubeViews: 3420,
  };
  
  // Initialize state - use preloaded stats if available, otherwise use defaults
  const initialStats: PlatformStats = preloadedStats || (isDemo ? DEMO_STATS : {
    zillow: zillowViews,
    zillowSaves: 0,
    realtor: 0,
    realtorSaves: 0,
    homes: 0,
    homesSaves: 0,
    redfin: 0,
    redfinSaves: 0,
    trulia: 0,
    truliaSaves: 0,
    mls: 0,
    mlsSaves: 0,
    instagram: 0,
    facebook: 0,
    youtube: 0,
    youtubeViews: 0,
  });

  const [stats, setStats] = useState<PlatformStats>(initialStats);
  const [loading, setLoading] = useState(!isDemo && !preloadedStats); // Skip loading if we have preloaded stats
  const [northData, setNorthData] = useState<any>(null); // Store north data to check for total values

  // Function to convert hex to HSL with null safety
  const hexToHSL = (hex: string | null): string => {
    // Return default if hex is null or empty
    if (!hex) {
      return '220 60% 40%'; // Default blue color
    }

    // Remove # if present
    const cleanHex = hex.replace('#', '');

    // Validate hex format (must be 6 characters)
    if (cleanHex.length !== 6) {
      return '220 60% 40%'; // Default blue color for invalid format
    }

    // Convert to RGB
    const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
    const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
    const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };

  // Convert hex colors to HSL for proper CSS usage (with null safety)
  const secondaryHSL = hexToHSL(secondaryColor);
  
  // For the background, use the exact light blue from the published report
  // rgb(221, 228, 240) = hsl(220, 42%, 90%)
  const lightBackgroundHSL = '220 42% 90%';
  
  // For text, use the exact dark blue from the published report
  // rgb(27, 72, 155) = hsl(219, 70%, 36%)
  const textColorHSL = '219 70% 36%';

  useEffect(() => {
    // For demo reports, data is already initialized - no need to fetch
    if (isDemo) {
      return;
    }
    
    // If we have preloaded stats, use them and skip fetching
    if (preloadedStats) {
      setStats(preloadedStats);
      setLoading(false);
      return;
    }
    
    const fetchSocialStats = async () => {
      try {
        // Fetch Zillow and Realtor.com metrics from norths table
        let realtorViews = 0;
        let realtorSaves = 0;
        let zillowSaves = 0;
        let fetchedNorthData: any = null;
        const northIdToFetch = northId;

        try {
          const { data, error: northError } = await supabase
            .from('norths')
            .select('*')
            .eq('id', northIdToFetch)
            .maybeSingle();

          if (!northError && data) {
            fetchedNorthData = data;
            setNorthData(data); // Store for later use in display
            // Use total values if available, otherwise use 30-day values
            realtorViews = data.total_realtor_views ?? data.realtor_views ?? 0;
            realtorSaves = data.total_realtor_saves ?? data.realtor_saves ?? 0;
            zillowSaves = data.total_zillow_saves ?? data.saves_count ?? 0;
          }
        } catch (realtorError) {
          logger.error('Error fetching metrics:', realtorError);
        }

        // Fetch via junction table (new architecture)
        let posts = null;
        const { data: junctionData, error: junctionError } = await supabase
          .from("north_social_posts")
          .select("social_post_id")
          .eq("north_id", northId);

        if (junctionError) throw junctionError;

        if (junctionData && junctionData.length > 0) {
          const postIds = junctionData.map(item => item.social_post_id);
          // Count every post linked through north_social_posts. That junction
          // is the curation signal — togglePostInReport writes it, and the UI
          // derives "added to report" from membership in it. Do NOT filter on
          // social_media_posts.is_added_to_report: that column is vestigial
          // (only fetch-social-post-by-url still sets it) and filtering on it
          // is what made the public report disagree with this screen.
          const { data, error } = await supabase
            .from('social_media_posts')
            .select('platform, engagement, reach, shares_count, views_count')
            .in('id', postIds);

          if (error) throw error;
          posts = data;
        }

        if (posts) {
          const platformStats = posts.reduce((acc, post) => {
            const platform = post.platform.toLowerCase();
            if (platform in acc) {
              // Aggregate engagement: Engagement + Reach + Shares for all platforms
              const engagement = post.engagement || 0;
              const reach = post.reach || 0;
              const shares = post.shares_count || 0;
              const totalEngagement = engagement + reach + shares;
              acc[platform as keyof PlatformStats] += totalEngagement;
              
              // Also track YouTube views separately
              if (platform === 'youtube') {
                acc.youtubeViews += post.views_count || 0;
              }
            }
            return acc;
          }, {
            zillow: zillowViews,
            zillowSaves: zillowSaves,
            realtor: realtorViews,
            realtorSaves: realtorSaves,
            homes: 0,
            homesSaves: 0,
            redfin: 0,
            redfinSaves: 0,
            trulia: 0,
            truliaSaves: 0,
            mls: 0,
            mlsSaves: 0,
            instagram: 0,
            facebook: 0,
            youtube: 0,
            youtubeViews: 0,
          } as PlatformStats);

          // Update zillow views if total is available (use fetchedNorthData, not stale state)
          if (fetchedNorthData?.total_zillow_views !== null && fetchedNorthData?.total_zillow_views !== undefined) {
            platformStats.zillow = fetchedNorthData.total_zillow_views;
          }
          // Set new portal platform stats from fetched data
          platformStats.homes = fetchedNorthData?.total_homes_views ?? 0;
          platformStats.homesSaves = fetchedNorthData?.total_homes_saves ?? 0;
          platformStats.redfin = fetchedNorthData?.total_redfin_views ?? 0;
          platformStats.redfinSaves = fetchedNorthData?.total_redfin_saves ?? 0;
          platformStats.trulia = fetchedNorthData?.total_trulia_views ?? 0;
          platformStats.truliaSaves = fetchedNorthData?.total_trulia_saves ?? 0;
          platformStats.mls = fetchedNorthData?.total_mls_views ?? 0;
          platformStats.mlsSaves = fetchedNorthData?.total_mls_saves ?? 0;

          setStats(platformStats);
        } else {
          // If no posts, still set stats with portal data
          const baseStats: PlatformStats = {
            zillow: zillowViews,
            zillowSaves: zillowSaves,
            realtor: realtorViews,
            realtorSaves: realtorSaves,
            homes: 0,
            homesSaves: 0,
            redfin: 0,
            redfinSaves: 0,
            trulia: 0,
            truliaSaves: 0,
            mls: 0,
            mlsSaves: 0,
            instagram: 0,
            facebook: 0,
            youtube: 0,
            youtubeViews: 0,
          };

          // Update zillow views if total is available (use fetchedNorthData, not stale state)
          if (fetchedNorthData?.total_zillow_views !== null && fetchedNorthData?.total_zillow_views !== undefined) {
            baseStats.zillow = fetchedNorthData.total_zillow_views;
          }
          // Set new portal platform stats from fetched data
          baseStats.homes = fetchedNorthData?.total_homes_views ?? 0;
          baseStats.homesSaves = fetchedNorthData?.total_homes_saves ?? 0;
          baseStats.redfin = fetchedNorthData?.total_redfin_views ?? 0;
          baseStats.redfinSaves = fetchedNorthData?.total_redfin_saves ?? 0;
          baseStats.trulia = fetchedNorthData?.total_trulia_views ?? 0;
          baseStats.truliaSaves = fetchedNorthData?.total_trulia_saves ?? 0;
          baseStats.mls = fetchedNorthData?.total_mls_views ?? 0;
          baseStats.mlsSaves = fetchedNorthData?.total_mls_saves ?? 0;

          setStats(baseStats);
        }
      } catch (error) {
        logger.error('Error fetching social stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSocialStats();
  }, [northId, zillowViews, isDemo, preloadedStats]);
  
  // Also re-fetch when component mounts or when explicitly needed
  // This ensures we get fresh data after a refresh
  useEffect(() => {
    let debounceTimer: NodeJS.Timeout | null = null;
    
    // Skip real-time updates for demo reports
    if (isDemo) return;
    
    // Force a re-fetch by invalidating the query
    const refetch = async () => {
      try {
        const northIdToFetch = northId;
        
        const { data: fetchedNorthData } = await supabase
          .from('norths')
          .select('*')
          .eq('id', northIdToFetch)
          .maybeSingle();

        if (fetchedNorthData) {
          setNorthData(fetchedNorthData); // Store for later use in display
          // Update stats if we got fresh data - use total values if available, otherwise use 30-day values
          setStats(prev => ({
            ...prev,
            zillow: fetchedNorthData.total_zillow_views ?? fetchedNorthData.views_count ?? zillowViews,
            zillowSaves: fetchedNorthData.total_zillow_saves ?? fetchedNorthData.saves_count ?? 0,
            realtor: fetchedNorthData.total_realtor_views ?? fetchedNorthData.realtor_views ?? 0,
            realtorSaves: fetchedNorthData.total_realtor_saves ?? fetchedNorthData.realtor_saves ?? 0,
            homes: fetchedNorthData.total_homes_views ?? 0,
            homesSaves: fetchedNorthData.total_homes_saves ?? 0,
            redfin: fetchedNorthData.total_redfin_views ?? 0,
            redfinSaves: fetchedNorthData.total_redfin_saves ?? 0,
            trulia: fetchedNorthData.total_trulia_views ?? 0,
            truliaSaves: fetchedNorthData.total_trulia_saves ?? 0,
            mls: fetchedNorthData.total_mls_views ?? 0,
            mlsSaves: fetchedNorthData.total_mls_saves ?? 0,
          }));
        }
      } catch (error) {
        logger.error('Error refetching metrics:', error);
      }
    };
    
    // Debounced refetch to prevent cascading updates
    const debouncedRefetch = () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        logger.debug('🔄 ListingPerformance: North updated, refetching metrics...');
        refetch();
      }, 500); // Wait 500ms after last update before refetching
    };
    
    // Set up a listener for norths table changes
    const channel = supabase
      .channel(`north-metrics-${northId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'norths',
          filter: `id=eq.${northId}`,
        },
        (payload) => {
          // Only refetch if relevant metrics fields changed
          const oldData = payload.old as any;
          const newData = payload.new as any;
          
          const metricsChanged =
            (oldData?.realtor_views !== newData?.realtor_views) ||
            (oldData?.realtor_saves !== newData?.realtor_saves) ||
            (oldData?.views_count !== newData?.views_count) ||
            (oldData?.saves_count !== newData?.saves_count) ||
            (oldData?.total_zillow_views !== newData?.total_zillow_views) ||
            (oldData?.total_zillow_saves !== newData?.total_zillow_saves) ||
            (oldData?.total_realtor_views !== newData?.total_realtor_views) ||
            (oldData?.total_realtor_saves !== newData?.total_realtor_saves) ||
            (oldData?.total_homes_views !== newData?.total_homes_views) ||
            (oldData?.total_homes_saves !== newData?.total_homes_saves) ||
            (oldData?.total_redfin_views !== newData?.total_redfin_views) ||
            (oldData?.total_redfin_saves !== newData?.total_redfin_saves) ||
            (oldData?.total_trulia_views !== newData?.total_trulia_views) ||
            (oldData?.total_trulia_saves !== newData?.total_trulia_saves) ||
            (oldData?.total_mls_views !== newData?.total_mls_views) ||
            (oldData?.total_mls_saves !== newData?.total_mls_saves);
          
          if (metricsChanged) {
            debouncedRefetch();
          }
        }
      )
      .subscribe();

    // Also listen for custom events from ViewsSavesManager
    const handleMetricsUpdate = (event: CustomEvent) => {
      if (event.detail?.northId === northId) {
        logger.debug('🔄 ListingPerformance: Custom metrics update event received, refetching immediately...');
        // Call refetch immediately (no debounce) since this is a direct user action
        refetch();
      }
    };
    
    window.addEventListener('north-metrics-updated', handleMetricsUpdate as EventListener);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener('north-metrics-updated', handleMetricsUpdate as EventListener);
    };
  }, [northId, zillowViews, isDemo]);

  const totalViews = (stats.zillow || 0) + (stats.realtor || 0) + (stats.homes || 0) + (stats.redfin || 0) + (stats.trulia || 0) + (stats.mls || 0) + (stats.instagram || 0) + (stats.facebook || 0) + (stats.youtube || 0) + (stats.youtubeViews || 0);

  const formatAsOfDate = (dateStr: string | null | undefined): string | null => {
    if (!dateStr) return null;
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Build platform list with views and saves
  const platforms: Array<{
    name: string;
    icon: typeof ZillowIcon | typeof RealtorIcon | typeof Instagram | typeof Facebook | typeof Youtube;
    count: number;
    key: string;
    isSave?: boolean;
    asOfDate?: string | null;
  }> = [];

  // Add Zillow Views
  if (stats.zillow > 0) {
    const hasTotal = northData?.total_zillow_views !== null && northData?.total_zillow_views !== undefined;
    const displayName = hasTotal ? 'Zillow Views' : 'Zillow Views (30 days)';
    platforms.push({ name: displayName, icon: ZillowIcon, count: stats.zillow, key: 'zillow-views', asOfDate: formatAsOfDate(northData?.zillow_views_as_of) });
  }
  // Add Zillow Saves
  if (stats.zillowSaves > 0) {
    const hasTotal = northData?.total_zillow_saves !== null && northData?.total_zillow_saves !== undefined;
    const displayName = hasTotal ? 'Zillow Saves' : 'Zillow Saves (30 days)';
    platforms.push({ name: displayName, icon: ZillowIcon, count: stats.zillowSaves, key: 'zillow-saves', isSave: true, asOfDate: formatAsOfDate(northData?.zillow_views_as_of) });
  }
  // Add Realtor.com Views
  if (stats.realtor > 0) {
    const hasTotal = northData?.total_realtor_views !== null && northData?.total_realtor_views !== undefined;
    const displayName = hasTotal ? 'Realtor.com Views' : 'Realtor.com Views (30 days)';
    platforms.push({ name: displayName, icon: RealtorIcon, count: stats.realtor, key: 'realtor-views', asOfDate: formatAsOfDate(northData?.realtor_views_as_of) });
  }
  // Add Realtor.com Saves
  if (stats.realtorSaves > 0) {
    const hasTotal = northData?.total_realtor_saves !== null && northData?.total_realtor_saves !== undefined;
    const displayName = hasTotal ? 'Realtor.com Saves' : 'Realtor.com Saves (30 days)';
    platforms.push({ name: displayName, icon: RealtorIcon, count: stats.realtorSaves, key: 'realtor-saves', isSave: true, asOfDate: formatAsOfDate(northData?.realtor_views_as_of) });
  }
  // Add Homes.com Views
  if (stats.homes > 0) {
    platforms.push({ name: 'Homes.com Views', icon: HomesIcon, count: stats.homes, key: 'homes-views', asOfDate: formatAsOfDate(northData?.homes_views_as_of) });
  }
  // Add Homes.com Saves
  if (stats.homesSaves > 0) {
    platforms.push({ name: 'Homes.com Saves', icon: HomesIcon, count: stats.homesSaves, key: 'homes-saves', isSave: true, asOfDate: formatAsOfDate(northData?.homes_views_as_of) });
  }
  // Add Redfin Views
  if (stats.redfin > 0) {
    platforms.push({ name: 'Redfin Views', icon: RedfinIcon, count: stats.redfin, key: 'redfin-views', asOfDate: formatAsOfDate(northData?.redfin_views_as_of) });
  }
  // Add Redfin Saves
  if (stats.redfinSaves > 0) {
    platforms.push({ name: 'Redfin Saves', icon: RedfinIcon, count: stats.redfinSaves, key: 'redfin-saves', isSave: true, asOfDate: formatAsOfDate(northData?.redfin_views_as_of) });
  }
  // Add Trulia Views
  if (stats.trulia > 0) {
    platforms.push({ name: 'Trulia Views', icon: TruliaIcon, count: stats.trulia, key: 'trulia-views', asOfDate: formatAsOfDate(northData?.trulia_views_as_of) });
  }
  // Add Trulia Saves
  if (stats.truliaSaves > 0) {
    platforms.push({ name: 'Trulia Saves', icon: TruliaIcon, count: stats.truliaSaves, key: 'trulia-saves', isSave: true, asOfDate: formatAsOfDate(northData?.trulia_views_as_of) });
  }
  // Add Local MLS Views
  if (stats.mls > 0) {
    platforms.push({ name: 'Local MLS Views', icon: MLSIcon, count: stats.mls, key: 'mls-views', asOfDate: formatAsOfDate(northData?.mls_views_as_of) });
  }
  // Add Local MLS Saves
  if (stats.mlsSaves > 0) {
    platforms.push({ name: 'Local MLS Saves', icon: MLSIcon, count: stats.mlsSaves, key: 'mls-saves', isSave: true, asOfDate: formatAsOfDate(northData?.mls_views_as_of) });
  }
  // Add other platforms
  if (stats.instagram > 0) {
    platforms.push({ name: 'Instagram', icon: Instagram, count: stats.instagram, key: 'instagram' });
  }
  if (stats.facebook > 0) {
    platforms.push({ name: 'Facebook', icon: Facebook, count: stats.facebook, key: 'facebook' });
  }
  if (stats.youtube > 0) {
    platforms.push({ name: 'YouTube', icon: Youtube, count: stats.youtube, key: 'youtube' });
  }
  // Add YouTube Views separately
  if (stats.youtubeViews > 0) {
    platforms.push({ name: 'YouTube Views', icon: Youtube, count: stats.youtubeViews, key: 'youtube-views' });
  }

  if (loading) {
    return (
      <div className="p-8 rounded-lg animate-pulse" style={{ backgroundColor: `hsl(${lightBackgroundHSL})` }}>
        <div className="h-8 bg-muted rounded w-48 mb-4"></div>
        <div className="h-20 bg-muted rounded"></div>
      </div>
    );
  }

  return (
    <div 
      className={shouldUseMobileLayout ? "p-4 rounded-lg" : "p-4 md:p-8 rounded-lg"}
      style={{ backgroundColor: `hsl(${lightBackgroundHSL})` }}
    >
      <div className="flex items-center gap-2 mb-4 md:mb-6">
        <h3 
          className={shouldUseMobileLayout ? "text-xs font-semibold tracking-wider" : "text-xs md:text-sm font-semibold tracking-wider"}
          style={{ color: `hsl(${textColorHSL})` }}
        >
          YOUR DIGITAL FOOTPRINT
        </h3>
        <span 
          className="text-[10px] md:text-xs text-muted-foreground"
          title="Zillow and Realtor.com only provide data from the last 30 days. Enter total values in the Views & Saves section to show all-time totals."
        >
          (Zillow & Realtor.com show last 30 days unless totals entered)
        </span>
      </div>
      
      <div className={shouldUseMobileLayout ? "flex flex-col gap-6" : "flex flex-col md:flex-row md:items-center gap-6 md:gap-8"}>
        {/* Total Views */}
        <div className={shouldUseMobileLayout ? "text-center flex-shrink-0" : "text-center md:text-left flex-shrink-0"}>
          <div 
            className={shouldUseMobileLayout ? "text-5xl font-bold mb-2" : "text-5xl md:text-7xl font-bold mb-2"}
            style={{ color: `hsl(${textColorHSL})` }}
          >
            {totalViews.toLocaleString()}
          </div>
          <p 
            className={shouldUseMobileLayout ? "text-xs font-medium text-foreground" : "text-xs md:text-sm font-medium text-foreground"}
          >
            Total Views of Your Listing
          </p>
        </div>

        {/* Platform Breakdown */}
        <div className={shouldUseMobileLayout ? "flex-1 w-full space-y-2" : "flex-1 w-full space-y-2 md:space-y-3"}>
          {platforms.map((platform, index) => {
            const Icon = platform.icon;
            const isLast = index === platforms.length - 1;
            const isSave = platform.isSave;
            return (
              <div 
                key={platform.key}
                className={`flex items-center justify-between py-2 ${shouldUseMobileLayout ? 'px-3' : 'px-3 md:px-4'} rounded-md ${!isLast ? 'border-b' : ''}`}
                style={{ 
                  borderBottomColor: !isLast ? `hsl(${textColorHSL} / 0.3)` : undefined,
                  color: `hsl(${textColorHSL})` 
                }}
              >
                <div className={shouldUseMobileLayout ? "flex items-center gap-2" : "flex items-center gap-2 md:gap-3"}>
                  {platform.key.startsWith('zillow') || platform.key.startsWith('realtor') || platform.key.startsWith('homes') || platform.key.startsWith('redfin') || platform.key.startsWith('trulia') || platform.key.startsWith('mls') ? (
                    <Icon className="h-5 w-5" />
                  ) : (
                    <Icon className="h-5 w-5" style={{ color: `hsl(${textColorHSL})` }} />
                  )}
                  <div>
                    <span className={shouldUseMobileLayout ? "text-sm font-medium" : "text-sm md:text-base font-medium"}>{platform.name}</span>
                    {platform.asOfDate && (
                      <span className="block text-[10px] md:text-xs text-muted-foreground">as of {platform.asOfDate}</span>
                    )}
                  </div>
                </div>
                <span className={shouldUseMobileLayout ? "text-xl font-bold" : "text-xl md:text-2xl font-bold"}>{platform.count.toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
